import { useState } from 'react'
import type { ModelUpgradeLimit } from '@/components/ui/image-model-picker'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog'
import { ImagePromptBox } from './common/image-prompt-box'
import { ImagePreviewContainer } from './common/image-preview-container'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { type GenerateImageOptions, type ImageResolution, getAvailableResolutions } from './types'

interface Props {
  isOpen: boolean
  currentImageUrl: string
  onClose: () => void
  onGenerate: (
    prompt: string,
    model: ImageModel,
    options?: GenerateImageOptions,
  ) => Promise<{ url: string; enhancedPrompt?: string }>
  onUseImage: (imageUrl: string) => void
  isGenerating: boolean
  isFreePlan?: boolean
  onUpgradeClick?: (limit: ModelUpgradeLimit) => void
}

export function WorkspaceEditorPopupEdit({
  isOpen,
  currentImageUrl,
  onClose,
  onGenerate,
  onUseImage,
  isGenerating,
  isFreePlan,
  onUpgradeClick,
}: Props) {
  const { theme } = useTheme()
  const [prompt, setPrompt] = useState('')
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string>()
  const [enhancedPrompt, setEnhancedPrompt] = useState<string>()
  const [imageModel, setImageModel] = useState<ImageModel>('gemini')
  const [referenceImages, setReferenceImages] = useState<string[]>([])
  const [flexSteps, setFlexSteps] = useState(50)
  const [flexGuidance, setFlexGuidance] = useState(5)
  const [resolution, setResolution] = useState<ImageResolution>('1K')

  if (!isOpen) return null

  const handleModelChange = (model: ImageModel) => {
    setImageModel(model)
    const available = getAvailableResolutions(model)
    if (!available.includes(resolution)) {
      setResolution(available[0]!)
    }
  }

  const handleGenerate = async () => {
    if (isGenerating) return
    if (!prompt.trim()) return

    try {
      const result = await onGenerate(prompt, imageModel, {
        referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
        steps: flexSteps,
        guidance: flexGuidance,
        resolution,
        enhance: true,
        useCaption: true,
        useColorPalette: true,
      })
      setGeneratedImageUrl(result.url)
      setEnhancedPrompt(result.enhancedPrompt)
    } catch (error) {
      console.error('Failed to generate image:', error)
    }
  }

  const handleUseImage = () => {
    if (generatedImageUrl) {
      onUseImage(generatedImageUrl)
      handleClose()
    }
  }

  const handleClose = () => {
    if (!isGenerating) {
      setPrompt('')
      setGeneratedImageUrl(undefined)
      setEnhancedPrompt(undefined)
      setImageModel('gemini')
      setReferenceImages([])
      setFlexSteps(50)
      setFlexGuidance(5)
      setResolution('1K')
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (generatedImageUrl) {
        handleUseImage()
      } else {
        handleGenerate()
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          'text-foreground-white flex h-[90vh] max-w-[1200px] flex-col border-transparent bg-transparent',
          theme === 'dark' ? 'dark' : '',
        )}>
        <DialogHeader>
          <DialogTitle className="text-foreground-white">Edit Image</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="flex min-h-0 flex-1 gap-4">
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <p className="text-foreground-white text-sm">Current</p>
              <ImagePreviewContainer imageUrl={currentImageUrl} alt="Current" />
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <p className="text-foreground-white text-sm">Preview</p>
              <ImagePreviewContainer
                imageUrl={generatedImageUrl}
                isGenerating={isGenerating}
                placeholderText="Generated image will appear here"
                alt="Generated"
                enhancedPrompt={enhancedPrompt}
              />
            </div>
          </div>

          <div className="mx-auto w-full max-w-[750px]">
            <ImagePromptBox
              prompt={prompt}
              onPromptChange={setPrompt}
              onKeyDown={handleKeyDown}
              placeholder="Describe how you want to modify this image..."
              isGenerating={isGenerating}
              hasGeneratedImage={!!generatedImageUrl}
              onCancel={handleClose}
              onGenerate={handleGenerate}
              onUseImage={handleUseImage}
              useImageButtonText="Accept"
              imageModel={imageModel}
              onModelChange={handleModelChange}
              referenceImages={referenceImages}
              onReferenceImagesChange={setReferenceImages}
              maxReferenceImages={7}
              flexSteps={flexSteps}
              onFlexStepsChange={setFlexSteps}
              flexGuidance={flexGuidance}
              onFlexGuidanceChange={setFlexGuidance}
              showCaptionAndColors={false}
              resolution={resolution}
              onResolutionChange={setResolution}
              isFreePlan={isFreePlan}
              onUpgradeClick={onUpgradeClick}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
